/**
 * Brand identifiers as the organization microservice delivers them in `salesforceBrand`.
 *
 * This is the identity of a brand and nothing else — deliberately free of any trait, so that both
 * sides of the boundary can key their own registry on it: the portal keys its assets and component
 * variants (`IrdBrand` in `@ird/ng-common`), the OTP mailer keys its SendGrid templates
 * (`IrdBrandTemplates` in the authentication domain). A single class carrying both would put asset
 * paths on the server and template ids in the browser bundle. The one exception is the manufacturer
 * name below: it is the brand's identity as a person writes it, and both sides need it.
 */
export enum IrdBrandValue {
  IR = 'IR',
  GD = 'GD',
}

/**
 * Falls back to IR for anything unrecognised — an unresolved tenant, a brand nothing has been
 * configured for, or a domain with no tenant behind it at all. Every consumer wants that same
 * fallback: the portal would otherwise render a hole where the logo goes, and the OTP webhook would
 * find no template and drop the mail entirely.
 */
export function resolveIrdBrandValue(value: string | null | undefined): IrdBrandValue {
  const normalized = value?.trim().toUpperCase();

  return Object.values(IrdBrandValue).find((brand) => brand === normalized) ?? IrdBrandValue.IR;
}

/**
 * Whether a brand was actually recognised, as opposed to falling back.
 *
 * `resolveIrdBrandValue` cannot distinguish "this caller sent no brand", which is expected, from
 * "this caller sent a brand we do not know", which is an integration bug that would otherwise
 * surface only as the wrong logo in an email. Callers that receive a brand from outside use this to
 * say so in a log line.
 */
export function isKnownIrdBrandValue(value: string | null | undefined): boolean {
  const normalized = value?.trim().toUpperCase() ?? '';

  return Object.values<string>(IrdBrandValue).includes(normalized);
}

/**
 * The manufacturer name each brand's equipment carries — a proper noun, never translated.
 *
 * Shared because both sides tell a brand's own equipment apart from anyone else's by this name: the
 * portal offers a serial lookup only for it, and the middleware guards only it against duplicating a
 * catalogue asset. A manufacturer typed as free text has no other way of saying which brand it is.
 */
export const IRD_BRAND_MANUFACTURER_NAMES: Readonly<Record<IrdBrandValue, string>> = {
  [IrdBrandValue.IR]: 'Ingersoll Rand',
  [IrdBrandValue.GD]: 'Gardner Denver',
};

/**
 * The brand whose manufacturer name this is, ignoring case and padding, or nothing for any other
 * manufacturer. Unlike `resolveIrdBrandValue` there is no fallback: another manufacturer is exactly
 * the answer a caller needs to be able to tell apart.
 */
export function resolveIrdBrandValueForManufacturer(manufacturer: string | null | undefined): IrdBrandValue | undefined {
  const normalized = manufacturer?.trim().toLowerCase();

  if (!normalized) {
    return undefined;
  }

  return Object.values(IrdBrandValue).find((brand) => IRD_BRAND_MANUFACTURER_NAMES[brand].toLowerCase() === normalized);
}
